/**
 * DISCOUNT-CODE PRICE — reveal the pre-rendered code price on the PDP.
 *
 * snippets/price.liquid renders a hidden `.price__code` block (the price
 * with the product's custom.promo_code applied) next to the regular/sale
 * markup, plus one `<discount-code-price data-code="…">` marker. This file
 * asks the cart JSON which discount codes are on the session — the only
 * storefront surface that exposes a /discount/CODE link's code — and toggles
 * `price--code-active` on the enclosing `.price` root when the product's code
 * is among them. Everything visual is CSS (component-price.css).
 *
 * Re-renders are free: the variant-change flow (product-info.js) and the
 * sticky-bar sync replace the price block's innerHTML, which re-connects the
 * marker and re-applies the cached answer. The cart JSON is fetched once per
 * page; a cart update (code removed in the drawer, for instance) drops the
 * cache and re-evaluates every marker.
 *
 * Auto-apply (opt-in per product, custom.promo_auto_apply, marker attribute
 * data-auto-apply): a visitor who lands on the PDP without the newsletter
 * link gets the product's code put on the cart session through
 * cart/update.js, so buybox and checkout match and a non-combinable code
 * keeps other codes from stacking. A visitor who already has the code (the
 * /discount/CODE link) causes no request. cart/update.js replaces the code
 * list with what it is sent, so the codes already on the session go along.
 * Once per page and code: a code the visitor removes is not forced back
 * until the next page view. Never in the theme editor.
 */
(() => {
  if (customElements.get('discount-code-price')) return;

  let codesPromise = null;
  const autoApplied = new Set();

  const cartJsonUrl = () => `${(window.routes && window.routes.cart_url) || '/cart'}.js`;
  const cartUpdateUrl = () => `${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`;

  const codesOf = (cart) =>
    new Set(
      (Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : []).map((entry) =>
        String((entry && entry.code) || '')
          .trim()
          .toUpperCase()
      )
    );

  const sessionCodes = () => {
    if (!codesPromise) {
      codesPromise = fetch(cartJsonUrl(), { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : { discount_codes: [] }))
        .then(codesOf)
        .catch(() => new Set());
    }
    return codesPromise;
  };

  // Synchronous bookkeeping on purpose: the PDP price block and the sticky bar
  // connect one after the other, and only the first may send the request; the
  // second reads the same (replaced) codesPromise.
  const applyCode = (code) => {
    autoApplied.add(code);
    codesPromise = sessionCodes().then((codes) => {
      if (codes.has(code)) return codes;
      return fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...codes, code].join(',') }),
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (!cart) return codes;
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'discount-code-price', cartData: cart, variantId: null });
          }
          return codesOf(cart);
        })
        .catch(() => codes);
    });
    return codesPromise;
  };

  class DiscountCodePrice extends HTMLElement {
    connectedCallback() {
      this.refresh();
    }

    refresh() {
      const code = (this.dataset.code || '').trim().toUpperCase();
      const priceRoot = this.closest('.price');
      if (!code || !priceRoot) return;
      const autoApply =
        this.hasAttribute('data-auto-apply') &&
        !(window.Shopify && window.Shopify.designMode) &&
        !autoApplied.has(code);
      (autoApply ? applyCode(code) : sessionCodes()).then((codes) => {
        if (!this.isConnected) return;
        priceRoot.classList.toggle('price--code-active', codes.has(code));
      });
    }
  }

  customElements.define('discount-code-price', DiscountCodePrice);

  // constants.js declares PUB_SUB_EVENTS with `const` — script-scope, not a window property.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      codesPromise = null;
      document.querySelectorAll('discount-code-price').forEach((marker) => marker.refresh());
    });
  }
})();
